import { FormFieldTypes, FormField, DescriptionTypes } from '../dynamic-form-fields';
import { OPTIONS } from './cioms-options';
import { COUNTRIES } from '@Constants/options';

// NOTE (2026-09-14): ROLLBACK of the 2026-09-03 "CIOMS: full section
// restructure/migration" (and its same-day follow-ups 3 and 4). That work
// copied Sections 1 and 3-13.2/15/16/17 verbatim from sae-aesi.ts into this
// file, added Weight/Height/Race/Other race/Ethnicity to Patient
// Information, converted Sex to a radio button, and split "Name and
// Address of PI" out of Manufacture Information into its own numbered
// heading. Per an explicit rollback request, all of that has been removed
// from this file and (where it originated there) restored to
// sae-aesi.ts's own field configuration, under its original section
// numbers - see the Change Log entry dated 2026-09-14 for the full
// before/after mapping.
//
// NOTE (2026-09-14, widened scope): the user's rollback request also
// covers the EARLIER 2026-09-01 (follow-up 6) removal of Reaction Onset,
// Describe Reaction(s), Check all appropriate to adverse reaction, and the
// entire "Suspect drug(s) information" and "Concomitant drug(s) and
// history" sections (this predates, and is independent of, the 2026-09-03
// migration described above, but the user wants CIOMS restored past that
// point too). All of these fields have been restored below, in their
// original position (directly after "Sex" and before "Manufacturer
// information") and using their original field keys/types, reconstructed
// from the Change Log's own description of them plus the option lists in
// `cioms-options.ts`, which were deliberately left in place when these
// fields were removed. Restoring them makes the pre-existing (never
// removed) `SAE_AESI_TO_CIOMS` mapping entries for these exact fields
// (backend/src/forms/helper/formaValueMappings/constants.ts) live again -
// no mapping-file change was needed. No field, key, option, or existing
// stored data was deleted anywhere - only this form's field CONFIGURATION
// changed. No backend schema, DTO, or database migration was touched by
// this rollback.
//
// NOTE (2026-09-14, follow-up correction): renamed the first section's
// header back to "Reaction Information" (its name before the 2026-09-01
// session that renamed it to "Patient Information" - the section's field
// list is otherwise unchanged). Split the single "Therapy Dates (from)/
// (to)" field into two separate INPUT fields (see the inline note above
// `therapyDate`'s definition for why they still share one underlying
// `data.therapyDate` array). Moved "Name and Address of PI" to the end of
// the form, after the follow-up detail list.
const CIOMS_FORM_FIELDS: FormField[] = [
  {
    label: 'Reaction Information',
    type: FormFieldTypes.DESCRIPTION,
    descriptionType: DescriptionTypes.H2,
  },
  {
    label: 'Patient Initials (first, last)',
    name: 'patient',
    type: FormFieldTypes.INPUT,
  },
  {
    label: 'Country',
    name: 'country',
    type: FormFieldTypes.SELECT,
    options: COUNTRIES,
  },
  {
    label: 'Date of Birth',
    name: 'dateOfBirth',
    type: FormFieldTypes.INPUT,
  },
  {
    label: 'Age (Years)',
    name: 'age',
    type: FormFieldTypes.INPUT_NUMBER,
  },
  {
    label: 'Sex',
    name: 'sex',
    type: FormFieldTypes.INPUT,
  },
  {
    label: 'Reaction Onset',
    name: 'reactionOnset',
    type: FormFieldTypes.INPUT,
  },
  {
    label: 'Describe Reaction(s) (including relevant tests/lab data)',
    name: 'describeReactions',
    type: FormFieldTypes.TEXT_AREA,
  },
  {
    label: 'Check all appropriate to adverse reaction',
    name: 'adverseReaction',
    type: FormFieldTypes.CHECK_BOX,
    options: OPTIONS.adverseReaction,
  },
  {
    label: 'Suspect drug(s) information',
    type: FormFieldTypes.DESCRIPTION,
    descriptionType: DescriptionTypes.H2,
  },
  {
    label: 'Suspect drug(s) (include generic name)',
    name: 'suspectDrugs',
    type: FormFieldTypes.INPUT,
  },
  {
    label: 'Daily dose(s)',
    name: 'dailyDoses',
    type: FormFieldTypes.INPUT,
  },
  {
    label: 'Route(s) of administration',
    name: 'routesOfAdministration',
    type: FormFieldTypes.INPUT,
  },
  {
    label: 'Indication(s) for use',
    name: 'indicationsForUse',
    type: FormFieldTypes.INPUT,
  },
  {
    // NOTE (2026-09-14): split into two plain-text INPUT fields ("from"
    // and "to"), matching every other date field in this app (e.g.
    // dateOfBirth, dateReport, and SAE_AESI's own investigationalProducts
    // startDate/stopDate are all plain INPUT - this app has never used an
    // actual date-picker component). Both fields write into the SAME
    // `data.therapyDate` array (index 0 = from, index 1 = to) via an
    // array `name` path, rather than two separate top-level keys - this
    // preserves compatibility with the existing `SAE_AESI_TO_CIOMS`
    // 'therapyDate' mappingFn (backend/src/forms/helper/
    // formaValueMappings/constants.ts), which already reads/writes
    // `data.therapyDate` as a `[startDate, stopDate]` array, and with
    // `cioms-to-csv.ts`/`cioms-to-pdf.ts`, which already read
    // `data.therapyDate` the same way - no mapping, CSV, or PDF change
    // was needed for this split.
    label: 'Therapy Dates (from)',
    name: ['therapyDate', 0],
    type: FormFieldTypes.INPUT,
  },
  {
    label: 'Therapy Dates (to)',
    name: ['therapyDate', 1],
    type: FormFieldTypes.INPUT,
  },
  {
    label: 'Therapy duration',
    name: 'therapyDuration',
    type: FormFieldTypes.INPUT,
  },
  {
    label: 'Did reaction abate after stopping drug?',
    name: 'reactionAbate',
    type: FormFieldTypes.RADIO,
    options: OPTIONS.reactionAbate,
  },
  {
    label: 'Did reaction reappear after reintroduction?',
    name: 'reactionReappear',
    type: FormFieldTypes.RADIO,
    options: OPTIONS.reactionReappear,
  },
  {
    label: 'Concomitant drug(s) and history',
    type: FormFieldTypes.DESCRIPTION,
    descriptionType: DescriptionTypes.H2,
  },
  {
    label: 'Concomitant drug(s) and dates of administration (exclude those used to treat reaction)',
    name: 'concomitantDrugs',
    type: FormFieldTypes.TEXT_AREA,
  },
  {
    label:
      'Other relevant history (e.g. diagnostics, allergics, pregancy with last month of period, etc.)',
    name: 'releventHistory',
    type: FormFieldTypes.TEXT_AREA,
  },
  {
    label: 'Manufacturer information',
    type: FormFieldTypes.DESCRIPTION,
    descriptionType: DescriptionTypes.H2,
  },
  {
    label: 'Name and address of manufacturer',
    name: 'manufacturer',
    type: FormFieldTypes.INPUT,
  },
  {
    label: 'MFR control No.',
    name: 'mrfNo',
    type: FormFieldTypes.INPUT,
  },
  {
    label: 'Date received by manufacturer',
    name: 'dateOfManufacturer',
    type: FormFieldTypes.INPUT,
  },
  {
    label: 'Report source',
    name: 'reportSource',
    type: FormFieldTypes.CHECK_BOX,
    options: OPTIONS.reportSource,
  },
  {
    label: 'Date of this report',
    name: 'dateReport',
    type: FormFieldTypes.INPUT,
  },
  {
    label: 'Report type',
    name: 'reportType',
    type: FormFieldTypes.RADIO,
    options: OPTIONS.reportType,
  },
  {
    label: 'Record follow-up report detail',
    addOrRemoveText: 'Follow Up Detail',
    name: 'followUpDetails',
    type: FormFieldTypes.FORM_LIST,
    children: [
      {
        label: 'Follow-up Number',
        name: 'followUpNumber',
        type: FormFieldTypes.INPUT,
      },
      {
        label: 'Data entry completed by',
        name: 'followUpDataEntry',
        type: FormFieldTypes.INPUT,
      },
      {
        label: 'QC entry completed by',
        name: 'followUpQcEntry',
        type: FormFieldTypes.INPUT,
      },
      {
        label: 'Medical Review completed by',
        name: 'followUpMedicalReview',
        type: FormFieldTypes.INPUT,
      },
    ],
  },
  {
    // NOTE (2026-09-14): moved to the end of the form, per explicit
    // request - the field itself, its name/key/type, is unchanged.
    label: 'Name and Address of PI',
    name: 'nameAndAddressOfPI',
    type: FormFieldTypes.INPUT,
  },
];

export { CIOMS_FORM_FIELDS };
